import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

// Extended AxiosRequestConfig with custom properties
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  errorContext?: string;
  skipToast?: boolean;
  retry?: {
    maxAttempts?: number;
    delayMs?: number;
  };
}
import { supabase } from '@/lib/supabase';
import { apiLogger as logger } from '@/lib/logger';
import {
  ApiResponse,
  PaginatedResponse,
  Agent,
  User,
  Workflow,
  Transaction,
  AgentReview,
  CreateAgentRequest,
  UpdateAgentRequest,
  CreateWorkflowRequest,
  CreateReviewRequest,
  FilterOptions,
  DashboardStats,
  Categories,
  FeaturedContent,
  UserRole,
} from '../types';
import {
  StandardApiResponse,
  isStandardApiResponse,
  isSuccessResponse,
  getFirstErrorMessage,
  ErrorCodes
} from '../types/api';

// API Configuration - Smart proxy handling
const getApiBaseUrl = () => {
  // In development, use proxy (relative path) for better development experience
  if (import.meta.env.DEV) {
    // Check if we should use local backend
    if (import.meta.env.VITE_API_BASE_URL?.includes('localhost') || import.meta.env.VITE_USE_LOCAL_API === 'true') {
      return import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
    }
    // If proxy target is specified, use it as a direct API URL instead of relying on Vite proxy
    if (import.meta.env.VITE_PROXY_TARGET) {
      return import.meta.env.VITE_PROXY_TARGET;
    }
    // Use Vite proxy for development (relative URLs)
    return '';  // Empty string uses relative URLs which Vite proxy will handle
  }
  
  // In production, always use the production API URL directly
  return import.meta.env.VITE_API_BASE_URL || 'https://agentsapi.chatslytics.com';
};

const API_BASE_URL = getApiBaseUrl();

class ApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Smart API URL configuration for dev/prod environments
    this.baseURL = getApiBaseUrl();
    
    if (import.meta.env.DEV) {
      if (this.baseURL === '') {
        logger.info('🔧 DEV MODE: Using Vite proxy for API calls (relative URLs)');
        logger.info('🎯 Proxy should forward to:', import.meta.env.VITE_PROXY_TARGET || 'https://agentsapi.chatslytics.com');
      } else {
        logger.info(`🔧 DEV MODE: Using direct API endpoint ${this.baseURL}`);
      }
    } else {
      logger.info(`🌐 PRODUCTION MODE: Using production API endpoint ${this.baseURL}`);
    }

    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 8000, // Reduce to 8 seconds for faster response times
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Setup interceptors
    this.setupInterceptors();
  }

  private async request<T>(endpoint: string, options: ExtendedAxiosRequestConfig = {}): Promise<T> {
    try {
      // Define public endpoints that don't require authentication
      const publicEndpoints = [
        '/health',
        '/api/v1/health',
        '/api/v1/agents',
        '/api/v1/categories',
        '/api/v1/auth/test',
        '/docs',
        '/openapi.json'
      ];
      
      const isPublicEndpoint = publicEndpoints.some(publicPath => 
        endpoint === publicPath || endpoint.startsWith(publicPath + '?')
      );

      // Get Supabase auth token with enhanced error handling
      let token = null;
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        try {
          // Get the access token from Supabase session
          token = session.access_token;

          if (import.meta.env.DEV) {
            logger.debug('🔑 Supabase Token for API request acquired');

            // Enhanced debugging for platform access endpoints
            if (endpoint.includes('/platforms/') && endpoint.includes('/access-check')) {
              console.log('🔍 PLATFORM ACCESS DEBUG:');
              console.log('  Endpoint:', endpoint);
              console.log('  Supabase User:', session.user?.email);
              console.log('  Supabase UID:', session.user?.id);
              console.log('  Token Length:', token?.length);
              console.log('  Token Preview:', token?.substring(0, 50) + '...');
              console.log('  Full Token:', token);
              console.log('  Authorization Header:', `Bearer ${token}`);
            }

            // Only log token details if explicitly debugging
            if (import.meta.env.VITE_DEBUG_AUTH === 'true') {
              logger.debug('🔑 Supabase Token for debugging: ' + token);
            }
          }
        } catch (tokenError) {
          logger.error('❌ Failed to get Supabase token:', tokenError);
          if (!isPublicEndpoint) {
            throw new Error('Authentication token unavailable');
          }
          logger.warn('⚠️ Proceeding without token for public endpoint:', endpoint);
        }
      } else {
        // For public endpoints, proceed without authentication
        // For private endpoints, only throw error if this isn't a graceful check
        if (!isPublicEndpoint) {
          logger.warn('⚠️ No Supabase user authenticated for:', endpoint);
          // Don't throw immediately - let the backend handle the 401/403 response
          // This allows for better error handling in components
        }
        if (isPublicEndpoint) {
          logger.info('ℹ️ Making unauthenticated request to public endpoint:', endpoint);
        }
      }

      const config: AxiosRequestConfig = {
        ...options,
        url: endpoint,
        headers: {
          ...options.headers,
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      };

      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_API === 'true') {
        logger.debug(`📡 Making API request: ${config.method?.toUpperCase() || 'GET'} ${endpoint}`);
      }

      const response = await this.client.request<T>(config);
      
      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_API === 'true') {
        logger.debug(`✅ API Success: ${response.status} for ${endpoint}`);
      }
      
      // Check if this is a standardized API response
      if (isStandardApiResponse((response as any).data)) {
        const standardResponse = (response as any).data as StandardApiResponse;

        if (!isSuccessResponse(standardResponse)) {
          const errorMessage = getFirstErrorMessage(standardResponse);
          const error = new Error(errorMessage);
          (error as any).apiResponse = standardResponse;
          (error as any).statusCode = standardResponse.status.code;
          throw error;
        }

        return standardResponse.data;
      }

      // Fallback for legacy responses
      return (response as any).data;
    } catch (error: any) {
      // Enhanced error handling for standardized responses
      if ((error as any).apiResponse) {
        const standardResponse = (error as any).apiResponse as StandardApiResponse;
        if (import.meta.env.DEV) {
          logger.error('Standardized API Error:', {
            endpoint,
            status: standardResponse.status,
            errors: standardResponse.errors,
            requestId: standardResponse.meta?.request_id
          });
        }
      }

      // Graceful error handling - determine if this is a critical or non-critical endpoint
      const isCriticalEndpoint = this.isCriticalEndpoint(endpoint);
      const errorType = this.categorizeError(error);
      
      // For non-critical endpoints, return graceful fallbacks
      if (!isCriticalEndpoint && !import.meta.env.DEV) {
        // Only use graceful fallbacks in production
        const gracefulResponse = this.getGracefulResponse(endpoint, errorType);
        if (gracefulResponse) {
          logger.warn(`🔄 Graceful fallback for ${endpoint}: ${errorType}`);
          return gracefulResponse as T;
        }
      }
      
      // In development, provide informative but less noisy error handling for auth endpoints
      if (import.meta.env.DEV) {
        // For auth-related endpoints with 401 errors, create a quieter error
        if (endpoint.includes('/users/me') && error.response?.status === 401) {
          logger.warn(`🔐 Authentication endpoint failed (${error.response.status}) - this is expected if backend is deploying or token is invalid:`, {
            errorType,
            endpoint,
            message: 'This error is normal during initial load or backend deployment'
          });
          
          // Create a quieter error for auth failures
          const authError = new Error('Authentication failed - expected during initial load');
          authError.name = 'AuthenticationError';
          (authError as any).response = error.response;
          (authError as any).isExpectedAuthFailure = true;
          throw authError;
        } else {
          logger.error(`❌ API Error (DEV MODE - no fallback): ${endpoint}`, {
            errorType,
            status: error.response?.status,
            message: error.message,
            url: `${this.baseURL}${endpoint}`
          });
        }
        // Re-throw the error in development so we can see it
        throw error;
      }
      
      // Enhanced error logging for debugging (only in dev mode)
      if (import.meta.env.DEV) {
        logger.error('❌ API Request Error Details:', {
          endpoint,
          method: options.method || 'GET',
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.message,
          errorType,
          responseData: error.response?.data
        });
      }
      
      // Only log error to console if it's a critical endpoint or in dev mode
      if (isCriticalEndpoint || import.meta.env.DEV) {
        logger.error('API Request Error:', error);
      }
      
      // Continue with existing error handling
      throw this.handleError(error);
    }
  }

  // Helper method to determine if an endpoint is critical
  private isCriticalEndpoint(endpoint: string): boolean {
    const criticalEndpoints = [
      '/auth/verify-token',
      '/auth/login',
      '/auth/logout'
    ];
    // /users/me is not critical because auth failures are expected during initial load
    return criticalEndpoints.some(critical => endpoint.includes(critical));
  }

  // Helper method to categorize error types
  private categorizeError(error: any): string {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return 'TIMEOUT';
    }
    if (error.code === 'ECONNREFUSED') {
      return 'CONNECTION_REFUSED';
    }
    if (error.code === 'ENOTFOUND') {
      return 'DNS_ERROR';
    }
    if (error.response?.status >= 500) {
      return 'SERVER_ERROR';
    }
    if (error.response?.status === 404) {
      return 'NOT_FOUND';
    }
    if (error.response?.status === 403) {
      return 'FORBIDDEN';
    }
    return 'UNKNOWN';
  }

  // Helper method to provide graceful responses for non-critical endpoints
  private getGracefulResponse(endpoint: string, errorType: string): any {
    // Platform access checks
    if (endpoint.includes('/platforms/') && endpoint.includes('/access-check')) {
      return {
        success: false,
        access_granted: false,
        message: errorType === 'TIMEOUT' 
          ? 'Platform temporarily unavailable (timeout)' 
          : 'Platform temporarily unavailable',
        error: 'PLATFORM_UNAVAILABLE'
      };
    }

    // Platform status checks
    if (endpoint.includes('/platforms/status')) {
      return {
        success: false,
        platforms: [],
        message: errorType === 'TIMEOUT' 
          ? 'Platform status check timed out' 
          : 'Platform status temporarily unavailable',
        error: 'PLATFORM_STATUS_UNAVAILABLE'
      };
    }

    // Health checks
    if (endpoint.includes('/health')) {
      return {
        status: 'unavailable',
        message: errorType === 'TIMEOUT' 
          ? 'Health check timed out' 
          : 'Service temporarily unavailable',
        timestamp: new Date().toISOString()
      };
    }

    // Agents list (non-critical - can show empty state)
    if (endpoint.includes('/agents') && !endpoint.includes('/agents/')) {
      return {
        agents: [],
        total: 0,
        page: 1,
        page_size: 20,
        has_next: false,
        has_previous: false,
        message: errorType === 'TIMEOUT' 
          ? 'Agents list temporarily unavailable (timeout)' 
          : 'Agents list temporarily unavailable'
      };
    }

    return null; // No graceful response available
  }

  private setupInterceptors() {
    // Request interceptor for logging in development
    this.client.interceptors.request.use(
      (config) => {
        if (import.meta.env.DEV) {
          const fullUrl = `${config.baseURL || ''}${config.url}`;
          logger.info(`🚀 API Request: ${config.method?.toUpperCase()} ${fullUrl}`);
          if (config.headers.Authorization) {
            logger.debug('   🔑 Auth header present');
          }
        }
        return config;
      },
      (error) => {
        logger.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => {
        if (import.meta.env.DEV) {
          const fullUrl = `${response.config.baseURL || ''}${response.config.url}`;
          logger.info(`✅ API Response: ${response.status} ${fullUrl}`);
        }
        return response;
      },
      (error) => {
        const url = error.config?.url || '';
        const isAuthEndpoint = url.includes('/users/me') || url.includes('/auth/');
        
        if (import.meta.env.DEV) {
          const fullUrl = `${error.config?.baseURL || ''}${error.config?.url}`;
          
          // For auth endpoints with 401 errors, provide a quieter log
          if (isAuthEndpoint && error.response?.status === 401) {
            logger.warn(`🔐 Auth endpoint 401 (expected during initial load): ${url}`);
          } else {
            logger.error(`❌ API Error: ${error.response?.status || 'NETWORK'} ${fullUrl}`, {
              status: error.response?.status,
              statusText: error.response?.statusText,
              message: error.message,
              code: error.code,
            });
          }
        }
        
        if (error.response?.status === 401) {
          // Don't redirect for platform access checks - these are expected to fail for users without access
          const isPlatformAccessCheck = url.includes('/platforms/') && url.includes('/access-check');
          const isPlatformStatus = url.includes('/platforms/status');
          
          if (!isPlatformAccessCheck && !isPlatformStatus && !isAuthEndpoint) {
            // For other 401 errors, just log but don't redirect (let components handle it)
            logger.warn('Received 401 error for:', url);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const url = error.config?.url || '';
      const message = (error as any).response?.data?.detail || 
                     (error as any).response?.data?.error || 
                     (error as any).response?.data?.message || 
                     'Server error';
      
      // More specific error handling for platform-related failures
      if (url.includes('/platforms/')) {
        if (status === 500) {
          return new Error('Platform service temporarily unavailable. Please try again later.');
        } else if (status === 403) {
          return new Error('You do not have permission to access this platform.');
        }
      }
      
      // Provide specific error messages for common HTTP status codes
      if (status === 404) {
        return new Error('The requested resource was not found. Please check the URL and try again.');
      } else if (status === 403) {
        return new Error('You do not have permission to access this resource.');
      } else if (status === 401) {
        return new Error('Authentication required. Please log in and try again.');
      } else if (status === 500) {
        return new Error('Server error. Please try again later.');
      } else if (status === 502 || status === 503 || status === 504) {
        return new Error('Service temporarily unavailable. Please try again later.');
      }
      
      return new Error(message);
    } else if (error.request) {
      // Request was made but no response received
      return new Error('Network error - please check your connection');
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  // ==========================================
  // Generic HTTP Methods
  // ==========================================

  async get<T>(url: string, options?: ExtendedAxiosRequestConfig): Promise<T> {
    return this.request<T>(url, { method: 'GET', ...options });
  }

  async post<T>(url: string, data?: any, options?: ExtendedAxiosRequestConfig): Promise<T> {
    return this.request<T>(url, { method: 'POST', data, ...options });
  }

  async put<T>(url: string, data?: any, options?: ExtendedAxiosRequestConfig): Promise<T> {
    return this.request<T>(url, { method: 'PUT', data, ...options });
  }

  async delete<T>(url: string, options?: ExtendedAxiosRequestConfig): Promise<T> {
    return this.request<T>(url, { method: 'DELETE', ...options });
  }

  // ==========================================
  // Standardized API Response Methods
  // ==========================================

  /**
   * Make a standardized API request that returns the full response structure
   */
  async requestStandard<T>(url: string, options?: AxiosRequestConfig): Promise<StandardApiResponse<T>> {
    // Prepare the request with authentication token
    const prepareRequest = async (): Promise<AxiosRequestConfig> => {
      const endpoint = url.startsWith('/') ? url : `/${url}`;

      // Check if this is a public endpoint that doesn't require authentication
      const publicEndpoints = [
        '/health',
        '/api/v1/health',
        '/api/v1/agents/marketplace',
        '/api/v1/categories',
        '/api/v1/auth/test',
        '/docs',
        '/openapi.json'
      ];

      const isPublicEndpoint = publicEndpoints.some(publicPath =>
        endpoint === publicPath || endpoint.startsWith(publicPath + '?')
      );

      let token: string | null = null;

      if (!isPublicEndpoint) {
        // Get authentication token
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          try {
            token = session.access_token;
          } catch (tokenError) {
            logger.warn('Failed to get Supabase token:', tokenError);
          }
        }
      }

      return {
        url: endpoint,
        ...options,
        headers: {
          ...options?.headers,
          ...(token && { Authorization: `Bearer ${token}` }),
        }
      };
    };

    try {
      const requestConfig = await prepareRequest();
      const response = await this.client.request<StandardApiResponse<T>>(requestConfig);

      if (!isStandardApiResponse<T>((response as any).data)) {
        throw new Error('Response is not in standardized format');
      }

      return (response as any).data;
    } catch (error: any) {
      // Auto-retry with fresh token for authentication errors
      if (error.response?.status === 403 || error.response?.status === 401) {
        logger.info('🔄 Authentication error detected, attempting token refresh and retry...');
        try {
          // Force refresh the Supabase token
          const { data: { session }, error } = await supabase.auth.refreshSession();
          if (session?.access_token && !error) {
            logger.info('🔑 Token refreshed successfully, retrying request...');

            // Retry the request once with fresh token
            const retryRequestConfig = await prepareRequest();
            const retryResponse = await this.client.request<StandardApiResponse<T>>(retryRequestConfig);

            if (!isStandardApiResponse<T>(retryResponse.data)) {
              throw new Error('Response is not in standardized format');
            }

            logger.info('✅ Request succeeded after token refresh');
            return retryResponse.data;
          }
        } catch (refreshError) {
          logger.error('❌ Token refresh failed:', refreshError);
          // Fall through to original error handling
        }
      }

      throw this.handleError(error);
    }
  }

  /**
   * Make a POST request expecting standardized response format
   */
  async postStandard<T>(url: string, data?: any, options?: ExtendedAxiosRequestConfig): Promise<StandardApiResponse<T>> {
    return this.requestStandard<T>(url, { method: 'POST', data, ...options });
  }

  /**
   * Make a GET request expecting standardized response format
   */
  async getStandard<T>(url: string, options?: ExtendedAxiosRequestConfig): Promise<StandardApiResponse<T>> {
    return this.requestStandard<T>(url, { method: 'GET', ...options });
  }

  /**
   * Make a PUT request expecting standardized response format
   */
  async putStandard<T>(url: string, data?: any, options?: ExtendedAxiosRequestConfig): Promise<StandardApiResponse<T>> {
    return this.requestStandard<T>(url, { method: 'PUT', data, ...options });
  }

  /**
   * Make a DELETE request expecting standardized response format
   */
  async deleteStandard<T>(url: string, options?: ExtendedAxiosRequestConfig): Promise<StandardApiResponse<T>> {
    return this.requestStandard<T>(url, { method: 'DELETE', ...options });
  }

  // ==========================================
  // Legacy Methods (for backward compatibility)
  // ==========================================

  // Auth methods (legacy)
  setToken(_token: string): void {
    // Legacy method - tokens are now managed by Supabase Auth
    console.warn('setToken is deprecated - using Supabase Auth');
  }

  clearToken(): void {
    // No-op: Supabase handles tokens
  }

  getToken(): string | null {
    // Return null as Supabase handles tokens
    return null;
  }

  // ==========================================
  // User Management
  // ==========================================

  async getCurrentUser(): Promise<User> {
    // Only log in development mode and provide quieter logging for auth endpoints
    if (import.meta.env.DEV) {
      console.log('🔍 API: Fetching current user (may fail during initial load)');
    }
    return this.request<User>('/api/v1/users/me', { 
      method: 'GET',
      timeout: 15000 // Increase timeout to 15 seconds for authentication processing
    });
  }

  async getUsers(params: { page?: number, limit?: number, role?: string, search?: string } = {}): Promise<{ users: User[], total: number }> {
    console.log('🔍 API: Fetching users with params:', params);
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.role) queryParams.append('role', params.role);
    if (params.search) queryParams.append('search', params.search);
    
    const queryString = queryParams.toString();
    const endpoint = `/api/v1/users${queryString ? `?${queryString}` : ''}`;
    const result = await this.request<{ users: User[], total: number }>(endpoint);
    console.log('✅ API: Users fetched successfully:', result.total, 'total users');
    return result;
  }

  async updateUserRole(userId: string, newRole: UserRole): Promise<User> {
    console.log('🔄 API: Updating user role:', userId, 'to', newRole);
    const result = await this.request<User>(`/api/v1/users/${userId}/role`, {
      method: 'PUT',
      data: { role: newRole }
    });
    console.log('✅ API: User role updated successfully');
    return result;
  }

  // User Management - Admin APIs
  async getPendingUsers(): Promise<User[]> {
    console.log('🔍 API: Fetching pending approval users');
    const result = await this.request<User[]>('/api/v1/user-management/pending-approval');
    console.log('✅ API: Pending users fetched:', result.length, 'users');
    return result;
  }

  async approveUser(userId: string, reason?: string): Promise<{ success: boolean, message: string }> {
    console.log('✅ API: Approving user:', userId, reason ? 'with reason' : 'without reason');
    const result = await this.request<{ success: boolean, message: string }>(`/api/v1/user-management/${userId}/approve`, {
      method: 'POST',
      data: { approve: true, reason }
    });
    console.log('✅ API: User approved successfully');
    return result;
  }

  async rejectUser(userId: string, reason: string): Promise<{ success: boolean, message: string }> {
    console.log('❌ API: Rejecting user:', userId, 'with reason:', reason);
    const result = await this.request<{ success: boolean, message: string }>(`/api/v1/user-management/${userId}/approve`, {
      method: 'POST',
      data: { approve: false, reason }
    });
    console.log('✅ API: User rejected successfully');
    return result;
  }

  async blockUser(userId: string, reason: string, durationDays?: number): Promise<{ success: boolean, message: string }> {
    console.log('🚫 API: Blocking user:', userId, 'for', durationDays || 'permanent', 'days');
    const result = await this.request<{ success: boolean, message: string }>(`/api/v1/user-management/${userId}/block`, {
      method: 'POST',
      data: { reason, duration_days: durationDays }
    });
    console.log('✅ API: User blocked successfully');
    return result;
  }

  async activateUser(userId: string, reason?: string): Promise<{ success: boolean, message: string }> {
    console.log('🟢 API: Activating user:', userId);
    const result = await this.request<{ success: boolean, message: string }>(`/api/v1/user-management/${userId}/activate`, {
      method: 'POST',
      data: { reason }
    });
    console.log('✅ API: User activated successfully');
    return result;
  }

  async upgradeUserRole(userId: string, newRole: UserRole, reason?: string): Promise<{ success: boolean, message: string, user: User }> {
    console.log('⬆️ API: Upgrading user role:', userId, 'to', newRole);
    const result = await this.request<{ success: boolean, message: string, user: User }>(`/api/v1/user-management/${userId}/upgrade-role`, {
      method: 'POST',
      data: { new_role: newRole, reason }
    });
    console.log('✅ API: User role upgraded successfully');
    return result;
  }

  async downgradeUserRole(userId: string, newRole: UserRole, reason: string): Promise<{ success: boolean, message: string, user: User }> {
    console.log('⬇️ API: Downgrading user role:', userId, 'to', newRole, 'reason:', reason);
    const result = await this.request<{ success: boolean, message: string, user: User }>(`/api/v1/user-management/${userId}/downgrade-role`, {
      method: 'POST',
      data: { new_role: newRole, reason }
    });
    console.log('✅ API: User role downgraded successfully');
    return result;
  }

  async getAssignableRoles(): Promise<{ assignable_roles: string[], current_user_role: string, role_hierarchy: Record<string, number> }> {
    console.log('🔍 API: Fetching assignable roles');
    const result = await this.request<{ assignable_roles: string[], current_user_role: string, role_hierarchy: Record<string, number> }>('/api/v1/user-management/assignable-roles');
    console.log('✅ API: Assignable roles fetched:', result.assignable_roles);
    return result;
  }

  async getBlockedUsers(): Promise<User[]> {
    console.log('🔍 API: Fetching blocked users');
    const result = await this.request<User[]>('/api/v1/user-management/blocked-users');
    console.log('✅ API: Blocked users fetched:', result.length, 'users');
    return result;
  }

  // ============================================================================
  // AUTHENTICATION ENDPOINTS
  // ============================================================================

  async verifyToken(): Promise<ApiResponse<User>> {
    return this.request<ApiResponse<User>>('/api/v1/auth/verify', { method: 'POST' });
  }

  async getProfile(): Promise<ApiResponse<User>> {
    return this.request<ApiResponse<User>>('/api/v1/auth/profile');
  }

  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    return this.request<ApiResponse<User>>('/api/v1/auth/profile', {
      method: 'PUT',
      data,
    });
  }

  // ==========================================
  // Agent Management
  // ==========================================

  // API Health Check with retry logic for 502/503 errors
  async checkHealth(): Promise<{status: string, online: boolean, version: string}> {
    const maxRetries = 3;
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const health = await this.request<{status: string, version: string}>('/api/v1/health');
        return {
          status: health.status,
          online: health.status === 'healthy',
          version: health.version
        };
      } catch (error: any) {
        lastError = error;
        
        // Only retry on server errors (502, 503, 504) that indicate temporary unavailability
        const shouldRetry = error?.response?.status === 502 || 
                           error?.response?.status === 503 || 
                           error?.response?.status === 504 ||
                           error?.code === 'ECONNREFUSED';
        
        if (shouldRetry && attempt < maxRetries) {
          // Wait with exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, attempt - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // If we shouldn't retry or have exhausted retries, break out
        break;
      }
    }

    // If all retries failed, return offline status
    return {
      status: 'offline',
      online: false,
      version: 'unknown'
    };
  }

  // Get list of agents
  async getAgents(params: FilterOptions = {}): Promise<PaginatedResponse<Agent>> {
    const queryString = new URLSearchParams(params as Record<string, string>).toString();
    const endpoint = `/api/v1/agents${queryString ? `?${queryString}` : ''}`;
    return this.request<PaginatedResponse<Agent>>(endpoint);
  }

  async getAgent(id: string): Promise<ApiResponse<Agent>> {
    return this.request<ApiResponse<Agent>>(`/api/v1/agents/${id}`);
  }

  async createAgent(data: CreateAgentRequest): Promise<ApiResponse<Agent>> {
    return this.request<ApiResponse<Agent>>('/api/v1/agents', {
      method: 'POST',
      data,
    });
  }

  async updateAgent(id: string, data: UpdateAgentRequest): Promise<ApiResponse<Agent>> {
    return this.request<ApiResponse<Agent>>(`/api/v1/agents/${id}`, {
      method: 'PUT',
      data,
    });
  }

  async deleteAgent(id: string): Promise<ApiResponse<null>> {
    return this.request<ApiResponse<null>>(`/api/v1/agents/${id}`, {
      method: 'DELETE',
    });
  }

  // ==========================================
  // Platform Access Management (Modern Workflow Builder)
  // ==========================================

  /**
   * Get status of all platforms
   */
  async getPlatformStatus(): Promise<ApiResponse<{ platforms: Array<{ platform: string; status: string; url: string; message: string; last_checked: string }> }>> {
    try {
      const response = await this.request<{ platforms: Array<{ platform: string; status: string; url: string; message: string; last_checked: string }> }>('/api/v1/platforms/status');
      return {
        success: true,
        data: response,
        message: 'Platform status retrieved',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Platform status check failed';
      return {
        success: false,
        data: { platforms: [] },
        message: errorMessage,
        timestamp: new Date().toISOString()
      };
    }
  }

  // ============================================================================
  // WORKFLOW ENDPOINTS
  // ============================================================================

  async getWorkflows(params: FilterOptions = {}): Promise<PaginatedResponse<Workflow>> {
    const queryString = new URLSearchParams(params as Record<string, string>).toString();
    const endpoint = `/api/v1/workflows${queryString ? `?${queryString}` : ''}`;
    return this.request<PaginatedResponse<Workflow>>(endpoint);
  }

  async getWorkflow(id: string): Promise<ApiResponse<Workflow>> {
    return this.request<ApiResponse<Workflow>>(`/api/v1/workflows/${id}`);
  }

  // ============================================================================
  // ADDITIONAL ENDPOINTS (to maintain compatibility)
  // ============================================================================

  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    return this.request<ApiResponse<DashboardStats>>('/api/v1/dashboard/stats');
  }

  async getCategories(_params?: any): Promise<ApiResponse<Categories>> {
    return this.request<ApiResponse<Categories>>('/api/v1/categories');
  }

  async getFeaturedContent(): Promise<ApiResponse<FeaturedContent>> {
    return this.request<ApiResponse<FeaturedContent>>('/api/v1/featured');
  }

  // ============================================================================
  // ENHANCED NODE REGISTRY V2 API
  // ============================================================================

  /**
   * Get Enhanced V2 node registry with advanced filtering
   */
  async getEnhancedNodeRegistry(filters?: any): Promise<ApiResponse<any>> {
    logger.info('📋 API: Fetching Enhanced V2 node registry');
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.has_visual !== undefined) params.append('has_visual', filters.has_visual.toString());
      if (filters?.has_cost_model !== undefined) params.append('has_cost_model', filters.has_cost_model.toString());
      if (filters?.enhanced_features !== undefined) params.append('enhanced_features', filters.enhanced_features.toString());
      if (filters?.search_term) params.append('search_term', filters.search_term);
      
      const queryString = params.toString();
      const endpoint = `/api/v1/nodes/registry/enhanced${queryString ? `?${queryString}` : ''}`;
      
      const result = await this.request<any>(endpoint, {
        method: 'GET',
        timeout: 10000
      });
      
      logger.info('✅ API: Enhanced V1 registry fetched successfully');
      return {
        success: true,
        data: result,
        message: 'Enhanced V1 registry fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('❌ API: Enhanced V1 registry fetch failed:', error);
      throw error;
    }
  }

  /**
   * Get Enhanced V1 node configuration
   */
  async getEnhancedNodeConfiguration(nodeType: string): Promise<ApiResponse<any>> {
    logger.info(`🔍 API: Fetching Enhanced V1 configuration for ${nodeType}`);
    try {
      const result = await this.request<any>(`/api/v1/nodes/${encodeURIComponent(nodeType)}/enhanced`, {
        method: 'GET',
        timeout: 5000
      });
      
      logger.info(`✅ API: Enhanced V1 configuration fetched for ${nodeType}`);
      return {
        success: true,
        data: result,
        message: 'Enhanced V1 configuration fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.warn(`⚠️ API: Enhanced V1 configuration not found for ${nodeType}`);
      return {
        success: false,
        data: {
          node_type: nodeType,
          status: 'missing',
          configuration: {},
          suggestions: [`Add Enhanced V1 configuration for ${nodeType} to database`]
        },
        message: `Enhanced V1 configuration not found: ${nodeType}`,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Get Enhanced V1 registry categories
   */
  async getEnhancedCategories(): Promise<ApiResponse<any>> {
    logger.info('📋 API: Fetching Enhanced V1 categories');
    try {
      const result = await this.request<any>('/api/v1/nodes/registry/categories', {
        method: 'GET',
        timeout: 5000
      });
      
      logger.info('✅ API: Enhanced V1 categories fetched successfully');
      return {
        success: true,
        data: result,
        message: 'Enhanced V1 categories fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('❌ API: Enhanced V1 categories fetch failed:', error);
      throw error;
    }
  }

  /**
   * Get Enhanced V1 registry statistics
   */
  async getEnhancedRegistryStats(): Promise<ApiResponse<any>> {
    logger.info('📊 API: Fetching Enhanced V1 registry statistics');
    try {
      const result = await this.request<any>('/api/v1/nodes/stats/enhanced', {
        method: 'GET',
        timeout: 8000
      });
      
      logger.info('✅ API: Enhanced V1 statistics fetched successfully');
      return {
        success: true,
        data: result,
        message: 'Enhanced V1 statistics fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('❌ API: Enhanced V1 statistics fetch failed:', error);
      throw error;
    }
  }

  /**
   * Validate Enhanced V1 node configuration
   */
  async validateEnhancedNodeConfiguration(config: any): Promise<ApiResponse<any>> {
    logger.info(`🔍 API: Validating Enhanced V1 configuration for ${config.node_type || 'unknown'}`);
    try {
      const result = await this.request<any>('/api/v1/nodes/validate/enhanced', {
        method: 'POST',
        data: config,
        timeout: 8000
      });
      
      logger.info(`✅ API: Enhanced V1 validation completed for ${config.node_type || 'unknown'}`);
      return {
        success: true,
        data: result,
        message: 'Enhanced V1 validation completed',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error(`❌ API: Enhanced V1 validation failed for ${config.node_type || 'unknown'}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // DATABASE-DRIVEN WORKFLOW SYSTEM API 
  // ============================================================================

  /**
   * Parse n8n workflow with database-driven node lookup
   */
  async parseWorkflow(workflowData: {
    workflow_data: any;
    user_id: string;
  }): Promise<ApiResponse<any>> {
    logger.info('🔄 API: Parsing workflow with database-driven node lookup');
    try {
      const result = await this.request<any>('/api/workflows/parse', {
        method: 'POST',
        data: workflowData,
        timeout: 15000 // Extended timeout for complex workflows
      });
      logger.info('✅ API: Workflow parsed successfully');
      return {
        success: true,
        data: result,
        message: 'Workflow parsed successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('❌ API: Workflow parsing failed:', error);
      throw error;
    }
  }

  /**
   * Analyze workflow without creating workflow object
   */
  async analyzeWorkflow(workflowData: {
    workflow_data: any;
    user_id: string;
  }): Promise<ApiResponse<any>> {
    logger.info('🔍 API: Analyzing workflow structure');
    try {
      const result = await this.request<any>('/api/workflows/analyze', {
        method: 'POST',
        data: workflowData,
        timeout: 10000
      });
      logger.info('✅ API: Workflow analysis completed');
      return {
        success: true,
        data: result,
        message: 'Workflow analysis completed',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('❌ API: Workflow analysis failed:', error);
      throw error;
    }
  }

  /**
   * Get all node configurations from database
   */
  async getNodeRegistry(): Promise<ApiResponse<any>> {
    logger.info('📋 API: Fetching node registry from database');
    try {
      // Use the public endpoint to avoid authentication issues
      const result = await this.request<any>('/api/nodes/registry/public', {
        method: 'GET',
        timeout: 8000
      });
      logger.info('✅ API: Node registry fetched successfully');
      return {
        success: true,
        data: result,
        message: 'Node registry fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('❌ API: Node registry fetch failed:', error);
      throw error;
    }
  }

  /**
   * Get specific node configuration from database
   */
  async getNodeConfiguration(nodeType: string): Promise<ApiResponse<any>> {
    logger.info(`🔍 API: Fetching configuration for node: ${nodeType}`);
    try {
      const result = await this.request<any>(`/api/nodes/${encodeURIComponent(nodeType)}/configuration`, {
        method: 'GET',
        timeout: 5000
      });
      logger.info(`✅ API: Node configuration fetched for: ${nodeType}`);
      return {
        success: true,
        data: result,
        message: 'Node configuration fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.warn(`⚠️ API: Node configuration not found for: ${nodeType}`);
      return {
        success: false,
        data: {
          node_type: nodeType,
          status: 'missing',
          configuration: {},
          suggestions: [`Add configuration for ${nodeType} to database`]
        },
        message: `Node configuration not found: ${nodeType}`,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Validate node configuration against database schema
   */
  async validateNodeConfiguration(data: {
    node_type: string;
    parameters: any;
  }): Promise<ApiResponse<any>> {
    logger.info(`🔍 API: Validating node configuration: ${data.node_type}`);
    try {
      const result = await this.request<any>('/api/nodes/validate', {
        method: 'POST',
        data,
        timeout: 5000
      });
      logger.info(`✅ API: Node validation completed: ${data.node_type}`);
      return {
        success: true,
        data: result,
        message: 'Node validation completed',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error(`❌ API: Node validation failed: ${data.node_type}`, error);
      throw error;
    }
  }

  /**
   * Get workflow configurations for specific workflow
   */
  async getWorkflowConfigurations(workflowId: string): Promise<ApiResponse<any>> {
    logger.info(`📋 API: Fetching configurations for workflow: ${workflowId}`);
    try {
      const result = await this.request<any>(`/api/workflows/${workflowId}/configurations`, {
        method: 'GET',
        timeout: 8000
      });
      logger.info(`✅ API: Workflow configurations fetched: ${workflowId}`);
      return {
        success: true,
        data: result,
        message: 'Workflow configurations fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error(`❌ API: Failed to fetch workflow configurations: ${workflowId}`, error);
      throw error;
    }
  }

  // ============================================================================
  // MCP & PLUGIN INTEGRATION API
  // ============================================================================

  /**
   * Get MCP server status and tools
   */
  async getMCPStatus(): Promise<ApiResponse<any>> {
    logger.info('🔧 API: Fetching MCP server status');
    try {
      const result = await this.request<any>('/api/mcp/status', {
        method: 'GET',
        timeout: 5000
      });
      logger.info('✅ API: MCP status fetched successfully');
      return {
        success: true,
        data: result,
        message: 'MCP status fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.warn('⚠️ API: MCP status fetch failed - MCP server may be unavailable');
      return {
        success: false,
        data: {
          mcp_servers: { connected: 0, available_tools: 0, servers: [] },
          plugins: { installed: 0, available: 0, plugins: [] },
          service_status: 'unavailable'
        },
        message: 'MCP server unavailable',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Convert workflow to AI agent
   */
  async convertWorkflowToAgent(workflowData: {
    workflow_data: any;
    user_id: string;
  }): Promise<ApiResponse<any>> {
    logger.info('🤖 API: Converting workflow to AI agent');
    try {
      const result = await this.request<any>('/api/workflows/convert-to-agent', {
        method: 'POST',
        data: workflowData,
        timeout: 20000 // Extended timeout for agent conversion
      });
      logger.info('✅ API: Workflow converted to AI agent successfully');
      return {
        success: true,
        data: result,
        message: 'Workflow converted to AI agent successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('❌ API: Workflow to agent conversion failed:', error);
      throw error;
    }
  }

  /**
   * Get plugin installation status
   */
  async getPluginStatus(pluginId?: string): Promise<ApiResponse<any>> {
    const endpoint = pluginId ? `/api/plugins/${pluginId}/status` : '/api/plugins/status';
    logger.info(`🔌 API: Fetching plugin status${pluginId ? ` for ${pluginId}` : ''}`);
    try {
      const result = await this.request<any>(endpoint, {
        method: 'GET',
        timeout: 5000
      });
      logger.info('✅ API: Plugin status fetched successfully');
      return {
        success: true,
        data: result,
        message: 'Plugin status fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.warn('⚠️ API: Plugin status fetch failed');
      return {
        success: false,
        data: { installed: false, available: false, status: 'unknown' },
        message: 'Plugin status unavailable',
        timestamp: new Date().toISOString()
      };
    }
  }

  // ============================================================================
  // MODERN WORKFLOW BUILDER API (Enhanced)
  // ============================================================================

  /**
   * Create a new workflow
   */
  async createWorkflow(workflow: {
    name: string;
    description: string;
    nodes: any[];
    edges: any[];
    metadata: any;
  }): Promise<ApiResponse<any>> {
    logger.info(`🔄 API: Creating new workflow: ${workflow.name}`);
    try {
      const result = await this.request<ApiResponse<any>>('/api/v1/workflows', {
        method: 'POST',
        data: workflow,
        timeout: 15000
      });
      logger.info(`✅ API: Workflow created: ${workflow.name}`);
      return result;
    } catch (error) {
      logger.error(`❌ API: Workflow creation failed: ${workflow.name}`, error);
      throw error;
    }
  }

  /**
   * Update an existing workflow
   */
  async updateWorkflow(id: string, workflow: {
    name?: string;
    description?: string;
    nodes?: any[];
    edges?: any[];
    metadata?: any;
  }): Promise<ApiResponse<any>> {
    logger.info(`🔄 API: Updating workflow: ${id}`);
    try {
      const result = await this.request<ApiResponse<any>>(`/api/v1/workflows/${id}`, {
        method: 'PUT',
        data: workflow,
        timeout: 15000
      });
      logger.info(`✅ API: Workflow updated: ${id}`);
      return result;
    } catch (error) {
      logger.error(`❌ API: Workflow update failed: ${id}`, error);
      throw error;
    }
  }

  /**
   * Execute a workflow
   */
  async executeWorkflow(workflowData: {
    nodes: any[];
    edges: any[];
    metadata: any;
  }): Promise<ApiResponse<any>> {
    logger.info('▶️ API: Executing workflow');
    try {
      const result = await this.request<ApiResponse<any>>('/api/v1/workflows/execute', {
        method: 'POST',
        data: workflowData,
        timeout: 30000 // Extended timeout for execution
      });
      logger.info('✅ API: Workflow execution started');
      return result;
    } catch (error) {
      logger.error('❌ API: Workflow execution failed:', error);
      throw error;
    }
  }

  /**
   * Get workflow execution status
   */
  async getWorkflowExecution(executionId: string): Promise<ApiResponse<any>> {
    logger.info(`📊 API: Fetching execution status: ${executionId}`);
    try {
      const result = await this.request<ApiResponse<any>>(`/api/v1/workflows/executions/${executionId}`);
      return result;
    } catch (error) {
      logger.error(`❌ API: Failed to fetch execution status: ${executionId}`, error);
      throw error;
    }
  }

  // ============================================================================
  // EXECUTION MANAGEMENT API
  // ============================================================================

  /**
   * Get all workflow executions with filtering
   */
  async getExecutions(params?: {
    status?: string;
    priority?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ executions: any[] }>> {
    logger.info('📋 API: Fetching workflow executions');
    try {
      const queryParams = new URLSearchParams();
      if (params?.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params?.priority && params.priority !== 'all') queryParams.append('priority', params.priority);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.page) queryParams.append('offset', ((params.page - 1) * (params.limit || 25)).toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      
      const queryString = queryParams.toString();
      const endpoint = `/api/v1/executions${queryString ? `?${queryString}` : ''}`;
      
      const result = await this.request<{ success: boolean; data: { items: any[]; total: number; limit: number; offset: number } }>(endpoint, {
        method: 'GET',
        timeout: 10000
      });
      
      logger.info('✅ API: Executions fetched successfully');
      
      // Transform backend response to match frontend expectations
      return {
        success: (result as any).success,
        data: {
          executions: (result as any).data.items || [] // Backend uses "items", frontend expects "executions"
        },
        message: 'Executions fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error('❌ API: Failed to fetch executions:', error);
      throw error;
    }
  }

  /**
   * Get execution details by ID
   */
  async getExecutionDetails(executionId: string): Promise<ApiResponse<any>> {
    logger.info(`🔍 API: Fetching execution details: ${executionId}`);
    try {
      const result = await this.request<any>(`/api/v1/executions/${executionId}`, {
        method: 'GET',
        timeout: 10000
      });
      
      logger.info(`✅ API: Execution details fetched: ${executionId}`);
      return {
        success: true,
        data: result,
        message: 'Execution details fetched successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error(`❌ API: Failed to fetch execution details: ${executionId}`, error);
      throw error;
    }
  }

  /**
   * Control workflow execution (start, stop, pause, resume)
   */
  async controlExecution(executionId: string, action: string): Promise<ApiResponse<any>> {
    logger.info(`🎮 API: Controlling execution ${executionId}: ${action}`);
    try {
      const result = await this.request<any>(`/api/v1/executions/${executionId}/control`, {
        method: 'POST',
        data: { action },
        timeout: 10000
      });
      
      logger.info(`✅ API: Execution control completed: ${executionId} - ${action}`);
      return {
        success: true,
        data: result,
        message: 'Execution control completed successfully',
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      logger.error(`❌ API: Execution control failed: ${executionId} - ${action}`, error);
      throw error;
    }
  }

  // ============================================================================
  // ENHANCED API METHODS (consolidated from enhancedApi.ts)
  // ============================================================================

  /**
   * Agent Templates API
   */
  async getAgentTemplates(params?: {
    category?: string;
    complexity?: string;
    is_premium?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/data-migration/templates/agents', {
      method: 'GET',
      params,
    });
  }

  /**
   * Workflow Templates API
   */
  async getWorkflowTemplates(params?: {
    category?: string;
    complexity?: string;
    is_premium?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/workflow-templates', {
      method: 'GET',
      params,
    });
  }

  async getWorkflowTemplate(templateId: string): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`/api/v1/workflow-templates/${templateId}`);
  }

  async getWorkflowTemplateCategories(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/workflow-templates/categories/list');
  }

  async getWorkflowTemplateStats(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/workflow-templates/stats/summary');
  }

  /**
   * Category API (enhanced)
   */
  async getCategory(categoryId: string): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`/api/v1/categories/${categoryId}`);
  }

  async getCategoryStats(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/categories/stats/summary');
  }

  /**
   * Integrations API
   */
  async getIntegrations(params?: {
    category?: string;
    provider?: string;
    is_premium?: boolean;
    required_plan?: string;
    skip?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/integrations', {
      method: 'GET',
      params,
    });
  }

  async getIntegration(integrationId: string): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`/api/v1/integrations/${integrationId}`);
  }

  async getIntegrationCategories(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/integrations/categories/list');
  }

  async getIntegrationProviders(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/integrations/providers/list');
  }

  async getIntegrationStats(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/integrations/stats/summary');
  }

  /**
   * Payments API (Razorpay)
   */
  async createPaymentOrder(orderData: {
    amount: number;
    currency?: string;
    receipt?: string;
    notes?: Record<string, string>;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/payments/create-order', {
      method: 'POST',
      data: orderData,
    });
  }

  async verifyPayment(paymentData: {
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/payments/verify', {
      method: 'POST',
      data: paymentData,
    });
  }

  async getPaymentHistory(params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/payments/history', {
      method: 'GET',
      params,
    });
  }

  /**
   * Subscriptions API
   */
  async getSubscriptionPlans(activeOnly: boolean = true): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/subscriptions/plans', {
      method: 'GET',
      params: { active_only: activeOnly },
    });
  }

  async getCurrentSubscription(): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/subscriptions/current');
  }

  async createSubscription(data: {
    plan_id: string;
    payment_method?: string;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/subscriptions/create', {
      method: 'POST',
      data,
    });
  }

  async cancelSubscription(subscriptionId: string): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>(`/api/v1/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
    });
  }

  async getSubscriptionHistory(params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<any>> {
    return this.request<ApiResponse<any>>('/api/v1/subscriptions/history', {
      method: 'GET',
      params,
    });
  }
}

// Export a singleton instance
const apiClient = new ApiClient();
export { apiClient };
export default apiClient;
